// Static editorial content for Cured & Clear (brand copy from the founder)

export const BRAND = {
  name: "Cured & Clear",
  logo: "https://static.prod-images.emergentagent.com/jobs/abf143d2-3bb8-4a2a-af45-7e99c0ea4245/images/6805d3b0d317e33dca9e9925a0dfa6800fd2617eb6c4e4e8f51370977e4702f6.jpeg",
  promise: "Quality you can read on the label and feel on your skin. No fillers. No shortcuts. No compromises on what goes on your body.",
  positioning:
    "Most brands lead with scent. We lead with ingredients. There is a difference. Cured & Clear is an ingredient-first luxury body butter brand — every ingredient we choose has a purpose. We do not prioritize scent over substance. Quality ingredients are always the priority.",
  about:
    "Cured & Clear was built for people who are done putting random products on their skin and are ready to start feeding it the way it deserves. Every formula is gentle enough for sensitive and eczema-prone skin and made to deliver deep, lasting moisture. Every butter we use is sourced with integrity — rooted in African tradition, backed by nature, and built to last.",
};

export const MANIFESTO = [
  {
    chapter: "01",
    title: "Ingredients Over Scent",
    quote: "Most brands lead with scent. We lead with ingredients.",
    body: "There is a difference. Fragrance is the cheapest way to make a product feel luxurious — and the easiest place to hide what is not. We refuse. Every ingredient in a Cured & Clear jar is chosen for what it does for your skin, never for how it masks the formula.",
  },
  {
    chapter: "02",
    title: "Sourced With Integrity",
    quote: "Rooted in tradition, backed by nature.",
    body: "Our butters are harvested directly across Africa using traditional methods passed down for generations — much of it wild-harvested by women-led cooperatives. Fair, direct, and built to last.",
  },
  {
    chapter: "03",
    title: "No Fillers. No Shortcuts.",
    quote: "Quality you can read on the label.",
    body: "No water padding. No synthetic bulk. No compromises on what goes on your body. What you read on the label is exactly what your skin feels — and that is the whole point.",
  },
];

export const GLOSSARY_SUBHEAD =
  "The butters and oils behind every jar — named, sourced, and explained. No exceptions.";

export const INGREDIENTS = [
  {
    name: "Kpangnan Butter",
    latin: "Pentadesma butyracea",
    origin: "West Africa",
    tag: "The Golden Hero",
    intro:
      "A rare butter harvested from the Pentadesma butyracea tree in West Africa.",
    sections: [
      {
        title: "Why Kpangnan Butter Is Different",
        body: "Unlike common butters that simply coat the skin, Kpangnan is prized for what's actually inside it. It's naturally rich in stigmasterol, a rare plant sterol, alongside a dense concentration of fatty acids and antioxidants.\n\nThe result is a butter that melts into skin on contact, absorbing without the heavy, greasy residue people expect from rich butters — leaving behind a soft, silky finish instead.\n\nKpangnan delivers moisture that lasts, making it a beautiful choice for dry, dehydrated, or seasonally stressed skin.",
      },
      {
        title: "Sourced With Integrity",
        body: "Our Kpangnan butter is sourced directly from West Africa, where it has been harvested and processed using traditional methods for generations. Choosing this butter isn't just about performance — it's about honoring where real skincare wisdom comes from, and supporting the communities who've cultivated this knowledge long before it reached the global market.",
      },
      {
        title: "How to Use It",
        body: "Because Kpangnan butter is the base of everything we make, you don't have to do anything extra to benefit from it — it's already doing the work in every jar, bar, or bottle.",
      },
    ],
    benefits: [
      "Rich and cushiony — a dense, golden texture long prized in African beauty rituals and massage formulas.",
      "Cocooning — forms a breathable layer that locks in moisture against dry air, wind, and everyday elements.",
      "Radiance — traditionally valued for skin that looks balanced, even, and luminous.",
    ],
    usedIn: ["Rich & Radiant Kpangnan Body Butter", "Every Cured & Clear product"],
    kind: "butter",
  },
  {
    name: "Nilotica Shea Butter",
    latin: "Vitellaria nilotica",
    origin: "East Africa",
    tag: "East African Rarity",
    intro:
      "Harvested from the Vitellaria nilotica tree in East Africa — creamier, softer, and richer than common shea.",
    sections: [
      {
        title: "Why Nilotica Is Different",
        body: "High in vitamins A, E, and K, and silky and fast-absorbing. Deeply moisturizing and gentle on sensitive skin, it leaves a soft, smooth, radiant finish.",
      },
      {
        title: "Sourced With Integrity",
        body: "Wild-harvested by women-led cooperatives using traditional methods passed down for generations.",
      },
    ],
    benefits: [
      "Deeply moisturizing",
      "Gentle on sensitive skin",
      "Silky, fast-absorbing texture",
      "Leaves a soft, radiant finish",
    ],
    usedIn: ["Ruth's Glow Body Butter", "Rich & Radiant (Nilotica variant)"],
    kind: "butter",
  },
  {
    name: "Marula Oil",
    latin: "Sclerocarya birrea",
    origin: "South Africa",
    tag: "The Oil Royalty Chose",
    intro:
      "Cold-pressed from the nut of the marula tree — Southern Africa's best-kept beauty secret for generations.",
    sections: [
      {
        title: "Why Marula Is Different",
        body: "Long before marula oil appeared in luxury skincare, it was the best kept beauty secret of Southern Africa. Cold-pressed from the nut of the marula tree — a tree so prized across the continent that communities have protected and harvested it for generations — this oil carries a nutrient profile that most synthetic moisturizers spend millions trying to replicate. Extraordinarily rich in oleic acid, palmitic acid, vitamins C and E, and rare amino acids that penetrate below the surface and feed the skin from within.\n\nIt absorbs instantly. It leaves no residue. It works on every skin type — dry, oily, sensitive, acne-prone — because it does not force moisture onto the skin. It communicates with it. The result is skin that feels like it found what it was missing.",
      },
    ],
    benefits: [
      "Instant-absorbing — sinks in fast and leaves no residue",
      "Every skin type — dry, oily, sensitive, and acne-prone alike",
      "Nutrient-dense — rich in oleic and palmitic acids, vitamins C and E, and rare amino acids",
    ],
    kind: "oil",
  },
  {
    name: "Baobab Oil",
    latin: "Adansonia digitata",
    origin: "Sub-Saharan Africa",
    tag: "From the Tree That Lives 1,000 Years",
    intro:
      "Cold-pressed from the seeds of Africa's Tree of Life — a tree that lives over a thousand years.",
    sections: [
      {
        title: "Why Baobab Is Different",
        body: "The Baobab tree does not just survive in Africa — it dominates it. Growing up to 30 meters wide, living over a thousand years, producing fruit that contains more vitamin C than an orange — this tree is called the Tree of Life for a reason. And the oil cold-pressed from its seeds carries that same density of life into your skin.\n\nPacked with omega fatty acids 3, 6, and 9 alongside vitamins A, C, D, E, and K — nutrients most carrier oils can only claim one or two of — Baobab oil is the rare ingredient that moisturizes, protects, firms, and brightens in a single application. It absorbs with a velvety finish that feels like nothing else.\n\nSkin that is chronically dry, reactive, or depleted responds to Baobab the way parched ground responds to rain — completely and immediately. This is not a trending ingredient. It is a thousand-year-old answer that the rest of the world is just now catching up to.",
      },
    ],
    benefits: [
      "Omega-rich — fatty acids 3, 6, and 9 plus vitamins A, C, D, E, and K",
      "Velvety finish — absorbs with a feel like nothing else",
      "Deeply moisturizing — a single application leaves skin soft and supple",
    ],
    kind: "oil",
  },
];

export const FAQS = [
  {
    q: "Where do you source your ingredients?",
    a: "Our butters are sourced directly from Africa using traditional harvesting methods that have been passed down for generations.",
  },
  {
    q: "Which product is right for my skin type?",
    a: "Both products are formulated for sensitive, oily, acne-prone, and combination skin. Rich & Radiant is gentle enough for eczema-prone skin and deeply moisturizing for very dry skin. Ruth's Glow is one gentle formula made for dry, sensitive, and reactive skin.",
  },
  {
    q: "Are your products greasy?",
    a: "No. Our customers consistently describe our body butter as non-greasy with a natural glow and long-lasting moisture.",
  },
  {
    q: "What makes Cured & Clear different?",
    a: "We are ingredient-first. We do not prioritize scent over substance — every ingredient we choose has a purpose. No fillers, no shortcuts, no compromises on what goes on your body.",
  },
  {
    q: "How should I use the body butter?",
    a: "Warm a small amount between your palms and massage into clean, slightly damp skin. A little goes a long way and absorbs without any greasy residue.",
  },
];

export const MARQUEE = [
  "Ingredient-First",
  "Rare African Butters",
  "No Fillers",
  "No Shortcuts",
  "Women-Led Cooperatives",
  "Ingredients Over Scent",
  "Non-Greasy Glow",
];

export const DISCLAIMER =
  "Disclaimer: The content provided on this website is for general educational and informational purposes only. The statements herein have not been evaluated by the Food and Drug Administration (FDA). The products and information offered are not intended to diagnose, treat, cure, or prevent any medical condition or disease. Users are strongly encouraged to conduct their own independent research regarding individual ingredients and to perform a patch test prior to initial application. If you are pregnant, nursing, managing a pre-existing medical condition, or taking prescription medications, please consult a qualified healthcare professional before using any new product.";

export const CONTACT = {
  email: "Cmerritt@curedandclear.com",
  address: "6031 E. Main St. Suite 118, Columbus, Ohio 43213",
};
